import React from 'react';
import '../styles/Legal.css';
import usePageMeta from "../hooks/usePageMeta";

const LAST_UPDATED = 'July 29, 2026';

const PrivacyPage: React.FC = () => {
  usePageMeta({
    title: "Privacy Statement | PabSync",
    description:
      "How PabSync collects, uses, and shares information across our website and application.",
    path: "/privacy",
  });

  return (
    <div className="legal-page">
      <main className="legal-container">
        <article className="legal-card">
          <h1 className="legal-title">Privacy Statement</h1>
          <p className="legal-meta">Last updated: {LAST_UPDATED}</p>

          <p>
            This Privacy Statement describes how PabSync collects, uses, and shares information when you use our
            website and our application.
          </p>

          <h2>Information we collect</h2>
          <ul>
            <li>
              <strong>Account information</strong> (e.g., name, email address) when you create an account or contact us.
            </li>
            <li>
              <strong>Product usage and content</strong> you provide in the app (e.g., tasks, projects, attachments).
            </li>
            <li>
              <strong>Device and log data</strong> (e.g., IP address, browser type, pages viewed) for security and
              troubleshooting.
            </li>
            <li>
              <strong>Billing data</strong> for paid plans (processed by our payment providers; we do not store full
              payment card numbers).
            </li>
          </ul>

          <h2>How we use information</h2>
          <ul>
            <li>Provide and operate the service (authentication, core features, support).</li>
            <li>Secure the service, prevent abuse, and investigate suspicious activity.</li>
            <li>Communicate with you about your account, important notices, and support requests.</li>
            <li>Process transactions for paid plans.</li>
          </ul>

          <h2>How we share information</h2>
          <p>
            We share information with service providers who help us operate the product (for example: hosting,
            analytics, email delivery, payments, and security). We may also share information if required by law.
          </p>
          <p>
            For a list of commonly used vendors, see our <a href="/subprocessors">Subprocessors</a> page.
          </p>

          <h2>Google Workspace integration</h2>
          <p>
            PabSync offers an optional Google Workspace integration that you connect from{' '}
            <strong>Account → Connections</strong>. When you connect it, PabSync accesses the
            following Google user data solely to provide the features you choose to use:
          </p>
          <ul>
            <li>
              <strong>Google account identity</strong> (email and basic profile) — to show which
              Google account is linked to your PabSync account.
            </li>
            <li>
              <strong>Google Calendar</strong> (<code>calendar.events</code>) — to create and update
              calendar events for tasks you choose to sync, and to display upcoming events on a task.
            </li>
            <li>
              <strong>Google Drive</strong> (<code>drive.file</code>) — to attach Drive files you
              explicitly select to a task and read those files’ metadata. We can access only the
              specific files you pick, not your wider Drive.
            </li>
          </ul>
          <p>
            <strong>How this data is used.</strong> Google Workspace data is used only to provide
            these user-facing features. We do not use it for advertising, and we do not use it to
            develop, improve, or train any artificial-intelligence or machine-learning models.
            PabSync’s AI features operate only on your PabSync content (tasks, projects, knowledge
            base) and never on your Drive or Calendar data.
          </p>
          <p>
            <strong>How this data is shared.</strong> We do not sell Google Workspace data or share
            it with third parties, data brokers, or advertisers. It is transmitted only between your
            browser, Google’s APIs, and PabSync’s own infrastructure to deliver the feature.
          </p>
          <p>
            <strong>How this data is protected and retained.</strong> OAuth tokens are encrypted at
            rest and transmitted over TLS. We retain only the tokens and the file/event references
            needed while the integration is connected. You can disconnect at any time from{' '}
            <strong>Account → Connections</strong>, which revokes and deletes the stored Google
            tokens; deleting your PabSync account removes the associated data.
          </p>
          <p>
            <strong>Limited Use.</strong> PabSync’s use of information received from Google APIs will
            adhere to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>

          <h2>Cookies and similar technologies</h2>
          <p>
            We use cookies and similar technologies to operate and secure the site and the app. For details, see our{' '}
            <a href="/cookies">Cookies Notice</a>.
          </p>

          <h2>Data retention</h2>
          <p>
            We retain information for as long as needed to provide the service, comply with legal obligations, resolve
            disputes, and enforce agreements. Retention depends on the type of data and how it’s used.
          </p>

          <h2>Your choices and privacy requests</h2>
          <p>
            Depending on where you live, you may have rights to access, delete, or export your information. To make a
            request, see <a href="/privacy-requests">Privacy Requests</a>.
          </p>

          <h2>Security</h2>
          <p>
            We use administrative, technical, and organizational safeguards designed to protect information. Learn more
            on our <a href="/security">Security</a> page.
          </p>

          <h2>Contact</h2>
          <p>
            Privacy questions: <a href="mailto:datacare@pabsync.com">datacare@pabsync.com</a>
          </p>

          <p className="legal-note">
            This page is provided for general informational purposes and may be updated from time to time.
          </p>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPage;
